import { Gauge } from 'lucide-react'
import type { ParametersRecord } from '../parameter-value'
import { UniversalNodeRegistry, type UniversalNodeSchema } from '../universal-node-schema'
import {
  SESSION_METRIC_TYPE,
  lowerSessionMetrics,
} from '@/features/backtest/utils/lower-session-metrics'

/**
 * The metrics a Session Metric node computes (Chris, 2026-10-07: "session
 * volume > 500k or change from the open % > 2 ... filters within the day").
 * Each is the value so far on every bar, never the day's final one.
 */
const METRICS = [
  {
    value: 'volumeToday',
    label: 'Volume so far today',
    readout: 'Volume summed over the session so far',
  },
  {
    value: 'changeFromOpen',
    label: 'Change from open %',
    readout: '(Close − Day open) ÷ Day open × 100',
  },
  {
    value: 'changeFromPrevClose',
    label: 'Change from previous close %',
    readout: '(Close − Previous close) ÷ Previous close × 100',
  },
  {
    value: 'gap',
    label: 'Gap %',
    readout: '(Day open − Previous close) ÷ Previous close × 100',
  },
  {
    value: 'distanceFromVwap',
    label: 'Distance from VWAP %',
    readout: '(Close − VWAP) ÷ VWAP × 100',
  },
  {
    value: 'positionInDayRange',
    label: 'Position in day range %',
    readout: 'Close between Low of day and High of day → 0 to 100%',
  },
] as const

const metricOf = (params: ParametersRecord) =>
  METRICS.find((m) => m.value === params.metric) ?? METRICS[1]

/**
 * A day-level number per bar, built from nodes the engine already has
 * (utils/lower-session-metrics.ts, the way the Trade Manager is lowered): the
 * reference levels the Form uses (the day's open, high and low so far, the
 * previous close), Rolling Sum, VWAP, Percent Change and Position in Range.
 * Kept out of the AI and EdgeScript catalogs for now.
 */
export const sessionMetricSchema: UniversalNodeSchema = {
  type: SESSION_METRIC_TYPE,
  compound: {
    engine: 'lowered',
    lower: lowerSessionMetrics,
    catalog: { ai: false, edgescript: false },
  },
  displayName: 'Session Metric',
  category: 'utilities',
  icon: Gauge,
  description:
    "A day's number on every bar, as of that bar: volume so far, change from the open or the previous close, gap, distance from VWAP, position in the day's range.",
  aliases: ['session volume', 'change from open', 'gap', 'vwap distance', 'day range'],
  inputHandler: { type: 'single' },
  inputs: [
    {
      id: 'input_1',
      displayName: 'Price data',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData'],
    },
  ],
  outputs: [{ id: 'value', displayName: 'Value', type: 'number' }],
  parameters: [
    {
      name: 'metric',
      displayName: 'Metric',
      type: 'select',
      default: 'changeFromOpen',
      options: METRICS.map(({ value, label }) => ({ value, label })),
    },
  ],
  ui: { showEquation: true },
  face: {
    // The metric's names are long ("Change from previous close %"): its
    // select takes the card's width, its name above it, as the Condition's
    // columns do.
    sections: [
      { title: 'Inputs', inputs: ['input_1'] },
      {
        title: 'Parameters',
        rows: [
          {
            label: '',
            params: ['metric'],
            layout: 'columns',
            labels: ['Metric'],
            widths: ['standard'],
          },
        ],
      },
    ],
    shortName: 'SESSION',
    // Named by its metric on pills and wires ("Change from open %").
    name: (params) => metricOf(params).label,
    equation: (context) => metricOf(context?.params ?? {}).readout,
  },
}

export function registerSessionMetricSchema(): void {
  UniversalNodeRegistry.register(sessionMetricSchema)
}
